const STORAGE_KEY = 'accountTickerThemeOverrides';
let overridesCache = null;

const isStorageAvailable = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const loadCache = () => {
  if (overridesCache !== null) {
    return overridesCache;
  }

  if (!isStorageAvailable()) {
    overridesCache = {};
    return overridesCache;
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    overridesCache = raw ? JSON.parse(raw) : {};
  } catch (error) {
    console.warn('[ThemeOverrides] Failed to load cache from localStorage', error);
    overridesCache = {};
  }

  return overridesCache;
};

const persistCache = () => {
  if (!isStorageAvailable()) {
    return;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(overridesCache));
  } catch (error) {
    console.warn('[ThemeOverrides] Failed to persist cache to localStorage', error);
  }
};

const getOverrideValue = (accountId, tickerId) => {
  if (accountId === undefined || accountId === null || tickerId === undefined || tickerId === null) {
    return undefined;
  }

  const cache = loadCache();
  const accountKey = String(accountId);
  const tickerKey = String(tickerId);

  if (!cache[accountKey]) {
    return undefined;
  }

  return cache[accountKey][tickerKey];
};

const setOverrideValue = (accountId, tickerId, themeEnabled) => {
  if (accountId === undefined || accountId === null || tickerId === undefined || tickerId === null) {
    return;
  }

  const cache = loadCache();
  const accountKey = String(accountId);
  const tickerKey = String(tickerId);

  if (!cache[accountKey]) {
    cache[accountKey] = {};
  }

  cache[accountKey][tickerKey] = themeEnabled;
  persistCache();
};

const removeOverrideValue = (accountId, tickerId) => {
  if (accountId === undefined || accountId === null || tickerId === undefined || tickerId === null) {
    return;
  }

  const cache = loadCache();
  const accountKey = String(accountId);
  const tickerKey = String(tickerId);

  if (cache[accountKey]) {
    delete cache[accountKey][tickerKey];
    if (Object.keys(cache[accountKey]).length === 0) {
      delete cache[accountKey];
    }
    persistCache();
  }
};

// Drops every stored override, not just one ticker's -- unlike
// removeOverrideValue (below), which was the only way to clear anything
// here and is never actually called anywhere in the app. Without a bulk
// clear, an override set in one browser session/user persists forever in
// this browser's localStorage: the next fetch's server value (even after
// it legitimately changes) is permanently overridden by the stale local
// value, since nothing ever reconciles or expires it. Call this on logout,
// same as chartDataCache.js's clearAllChartDataCaches().
const clearAllOverrides = () => {
  overridesCache = {};

  if (!isStorageAvailable()) {
    return;
  }

  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('[ThemeOverrides] Failed to clear cache from localStorage', error);
  }
};

const cloneIfNeeded = (ticker, override, targetKey = 'theme_enabled') => {
  if (typeof override !== 'boolean' || ticker[targetKey] === override) {
    return ticker;
  }
  return { ...ticker, [targetKey]: override };
};

const applyOverrideToTicker = (ticker, defaultAccountId = null, cache = null) => {
  if (!ticker) {
    return ticker;
  }

  const resolvedAccountId = ticker.account_id ?? defaultAccountId;
  let nextTicker = ticker;

  if (resolvedAccountId !== undefined && resolvedAccountId !== null) {
    const override = getOverrideValue(resolvedAccountId, ticker.ticker_id);
    nextTicker = cloneIfNeeded(nextTicker, override);
  }

  if (Array.isArray(ticker.accounts) && ticker.accounts.length > 0) {
    let accountsChanged = false;
    const updatedAccounts = ticker.accounts.map(accountEntry => {
      const override = getOverrideValue(accountEntry.account_id, ticker.ticker_id);
      if (typeof override !== 'boolean' || accountEntry.theme_enabled === override) {
        return accountEntry;
      }
      accountsChanged = true;
      return { ...accountEntry, theme_enabled: override };
    });

    if (accountsChanged) {
      if (nextTicker === ticker) {
        nextTicker = { ...ticker };
      }
      nextTicker.accounts = updatedAccounts;
    }
  }

  return nextTicker;
};

export const applyThemeOverridesToTickers = (tickers, defaultAccountId = null) => {
  if (!Array.isArray(tickers) || tickers.length === 0) {
    return tickers;
  }

  const cache = loadCache();
  return tickers.map(ticker => applyOverrideToTicker(ticker, defaultAccountId, cache));
};

export {
  getOverrideValue as getThemeOverride,
  setOverrideValue as setThemeOverride,
  removeOverrideValue as clearThemeOverride,
  clearAllOverrides as clearAllThemeOverrides
};
